// useQuote.js - Simple global state management
import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "cpq-quote-data";

export const useQuote = () => {
    const [data, setData] = useState({
        products: [],
        quoteId: "",
    });
    const [isLoading, setIsLoading] = useState(true);

    // Load from localStorage on mount
    useEffect(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                setData(parsed);
            }
        } catch (error) {
            console.error("Error loading quote data:", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Save to localStorage when data changes
    useEffect(() => {
        if (!isLoading) {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(data));

                // Notify other tabs/windows
                window.dispatchEvent(
                    new CustomEvent("quoteUpdated", {
                        detail: data,
                    })
                );
            } catch (error) {
                console.error("Error saving quote data:", error);
            }
        }
    }, [data, isLoading]);

    // Add product
    const addProduct = useCallback((productData) => {
        const newProduct = {
            id: `${productData.productType}-${Date.now()}-${Math.random()
                .toString(36)
                .substr(2, 9)}`,
            ...productData,
            addedAt: new Date().toISOString(),
        };

        setData((prev) => ({
            ...prev,
            products: [...prev.products, newProduct],
        }));

        return newProduct.id;
    }, []);

    // Remove product
    const removeProduct = useCallback((productId) => {
        setData((prev) => ({
            ...prev,
            products: prev.products.filter((p) => p.id !== productId),
        }));
    }, []);

    // Update quote ID
    const setQuoteId = useCallback((quoteId) => {
        setData((prev) => ({
            ...prev,
            quoteId,
        }));
    }, []);

    // Clear all
    const clearAll = useCallback(() => {
        setData({
            products: [],
            quoteId: "",
        });
    }, []);

    // Helper functions
    const getTotalValue = useCallback(() => {
        return data.products.reduce(
            (total, product) => total + (product.totalPrice || 0),
            0
        );
    }, [data.products]);

    const getProductCount = useCallback(() => {
        return data.products.length;
    }, [data.products]);

    return {
        // Data
        products: data.products,
        quoteId: data.quoteId,
        isLoading,

        // Actions
        addProduct,
        removeProduct,
        setQuoteId,
        clearAll,

        // Helpers
        getTotalValue,
        getProductCount,
    };
};
